/**
 * Schema.org structured data helpers per Colucci Assicuratori.
 *
 * Tutti i JSON-LD sono compatibili con Google Search (validati su Rich Results Test).
 * Uso: importare le factory e passarle come prop `jsonLd` a <SEOHead />.
 */

const BASE_URL = "https://www.colucciassicuratori.com";
const TELEPHONE = "+39 081 7611000";
const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Via Armando Diaz 182/L",
  addressLocality: "Portici",
  addressRegion: "NA",
  postalCode: "80055",
  addressCountry: "IT",
};

/** InsuranceAgency — identita' organizzazione (da emettere in HomePage). */
export function schemaOrganization() {
  return {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    "@id": `${BASE_URL}/#organization`,
    name: "Colucci Assicuratori",
    alternateName: "Cristian e Ciro Colucci S.a.s.",
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    image: `${BASE_URL}/og-image.png`,
    telephone: TELEPHONE,
    email: "info@colucciassicuratori.com",
    vatID: "07618011212",
    taxID: "07618011212",
    address: ADDRESS,
    areaServed: { "@type": "Country", name: "Italy" },
    priceRange: "€€",
    description:
      "Agenzia assicurativa di riferimento in Campania dal 1982. Consulenza personalizzata su polizze auto, casa, vita, salute, professionisti, imprese e noleggio lungo termine.",
    sameAs: [
      "https://www.instagram.com/colucci_assicuratori/",
      "https://www.facebook.com/colucciassicuratori",
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: TELEPHONE,
        contactType: "customer service",
        areaServed: "IT",
        availableLanguage: ["Italian"],
      },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "18:00",
      },
    ],
  };
}

/** WebSite con SearchAction — utile per sitelinks search box. */
export function schemaWebsite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    url: BASE_URL,
    name: "Colucci Assicuratori",
    inLanguage: "it-IT",
    publisher: { "@id": `${BASE_URL}/#organization` },
  };
}

/** BreadcrumbList — navigation structured data.
 * Chiamare con un array di { name, path } da root a foglia. */
export function schemaBreadcrumb(trail) {
  const items = trail.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: t.name,
    item: `${BASE_URL}${t.path.startsWith("/") ? t.path : "/" + t.path}`,
  }));
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

/** NewsArticle — per le pagine editoriali del magazine. */
export function schemaNewsArticle({
  headline,
  description,
  datePublished,
  dateModified,
  image,
  slug,
  authorName = "Redazione Colucci",
  sectionName = "Noleggio",
}) {
  const url = `${BASE_URL}/noleggio/novita/${slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline,
    description,
    image: image ? [image] : [`${BASE_URL}/og-image.png`],
    datePublished,
    dateModified: dateModified || datePublished,
    author: { "@type": "Organization", name: authorName, url: BASE_URL },
    publisher: {
      "@type": "Organization",
      name: "Colucci Assicuratori",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/logo.png` },
    },
    articleSection: sectionName,
    inLanguage: "it-IT",
  };
}

/** Service / Product — per le landing pagine prodotto (Auto, Casa, etc.). */
export function schemaService({ name, description, slug, serviceType = "InsuranceProduct" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    name,
    description,
    provider: { "@id": `${BASE_URL}/#organization` },
    areaServed: { "@type": "Country", name: "Italy" },
    url: `${BASE_URL}${slug.startsWith("/") ? slug : "/" + slug}`,
  };
}

/** FAQPage — per le pagine con sezioni FAQ. */
export function schemaFaq(qaList) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qaList.map((qa) => ({
      "@type": "Question",
      name: qa.q,
      acceptedAnswer: { "@type": "Answer", text: qa.a },
    })),
  };
}

/** Combina piu' schemi in un singolo @graph (best practice Google). */
export function combineSchemas(...schemas) {
  return {
    "@context": "https://schema.org",
    "@graph": schemas.filter(Boolean),
  };
}

export { BASE_URL };
